/**
 * Location Finder — pull stores from the trusscore.com map into a retailer node.
 *
 * trusscore.com cannot search stores by name, but it can list a whole BANNER
 * (buying group, e.g. "Home Hardware") in one call, optionally narrowed by
 * country / region / city. The results are filtered here, in the browser, by
 * anything typed (name, city, address, sfid), ticked, and added in one go
 * (the server takes 50 per call; this loops). Stores already in the list are
 * shown as such and can still be ticked — adding them moves them to this node.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, X, Store, CheckSquare, Square, AlertTriangle } from 'lucide-react';
import { lookupUpstream, importPartnerLocations, type UpstreamLocation, type ImportResult } from '../../../services/adminPartnersApi';
import { fetchBanners, type RetailerTreeNode } from '../../../services/adminRetailerTreeApi';

interface Props {
  /** The node the stores will be assigned to (null = just bring them in, unassigned). */
  target: RetailerTreeNode | null;
  /** Stores already in the list: id → the node they're assigned to (null = unassigned). */
  assignedTo: Map<number, string | null>;
  onClose: () => void;
  onImported: (summary: { added: number; moved: number; failed: number }) => void;
}

const BATCH = 50;

export const LocationFinderModal: React.FC<Props> = ({ target, assignedTo, onClose, onImported }) => {
  const [banners, setBanners] = useState<{ value: string; count: number }[]>([]);
  const [countries, setCountries] = useState<{ code: string; name: string; count: number }[]>([]);
  const [mode, setMode] = useState<'banner' | 'ids'>('banner');
  const [bg, setBg] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [ids, setIds] = useState('');
  const [results, setResults] = useState<UpstreamLocation[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBanners().then(b => { setBanners(b.banners); setCountries(b.countries); }).catch(() => { /* lookup still works by ids */ });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !importing) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, importing]);

  const search = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSearching(true); setNote(null); setError(null); setResults(null); setTicked(new Set());
    try {
      const q = mode === 'ids'
        ? { dataset: 'retailers' as const, ids: ids.split(/[\s,]+/).filter(Boolean).join(','), limit: 500 }
        : { dataset: 'retailers' as const, bg: bg || undefined, country: country || undefined, region: region.trim() || undefined, city: city.trim() || undefined, limit: 500 };
      if (mode === 'ids' && !q.ids) { setError('Paste at least one Salesforce id.'); return; }
      if (mode === 'banner' && !q.bg && !q.country && !q.region) { setError('Pick a banner, or at least a country or region.'); return; }
      const r = await lookupUpstream(q);
      if (r.notConfigured) { setNote('The trusscore.com endpoint is not configured (Settings → Integrations → Location Maps).'); setResults([]); return; }
      setResults(r.locations);
      if (r.stale) setNote('Showing cached results — trusscore.com did not answer just now.');
      if (r.total && r.total > r.locations.length) setNote(`trusscore.com has ${r.total} matching stores; showing the first ${r.locations.length}. Narrow by country or region to see the rest.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const shown = useMemo(() => {
    if (!results) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return results;
    return results.filter(u => [u.name, u.city, u.region, u.street, u.zip, u.sfid, u.id].some(v => (v ?? '').toLowerCase().includes(f)));
  }, [results, filter]);

  const key = (u: UpstreamLocation) => u.sfid || u.id;
  const allShownTicked = shown.length > 0 && shown.every(u => ticked.has(key(u)));
  const toggleAll = () => setTicked(prev => {
    const n = new Set(prev);
    if (allShownTicked) shown.forEach(u => n.delete(key(u))); else shown.forEach(u => n.add(key(u)));
    return n;
  });
  const toggleOne = (u: UpstreamLocation) => setTicked(prev => { const n = new Set(prev); const k = key(u); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const run = async () => {
    const list = Array.from(ticked);
    if (!list.length) return;
    setImporting({ done: 0, total: list.length }); setError(null);
    let added = 0, moved = 0, failed = 0;
    try {
      for (let i = 0; i < list.length; i += BATCH) {
        const r: ImportResult = await importPartnerLocations(list.slice(i, i + BATCH), target?.id ?? null);
        added += r.created.length; moved += r.existed.length; failed += r.failed.length;
        setImporting({ done: Math.min(i + BATCH, list.length), total: list.length });
      }
      onImported({ added, moved, failed });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setImporting(null);
    }
  };

  const statusOf = (u: UpstreamLocation) => {
    if (!u.linkedId) return null;
    const node = assignedTo.get(u.linkedId);
    if (node === undefined) return <span className="badge text-bg-light border">in the list</span>;
    if (node === null) return <span className="badge text-bg-warning">unassigned</span>;
    if (target && node === target.name) return <span className="badge text-bg-success">already here</span>;
    return <span className="badge text-bg-info" title="Adding it here moves it">{node}</span>;
  };

  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="finder-title">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2" id="finder-title"><Store size={18} /> Add locations{target ? <> to <strong>{target.name}</strong></> : ''}</h5>
              <button type="button" className="btn-close" aria-label="Close" disabled={!!importing} onClick={onClose} />
            </div>
            <div className="modal-body">
              <ul className="nav nav-pills gap-1 mb-3">
                <li className="nav-item"><button type="button" className={`nav-link py-1 px-3 ${mode === 'banner' ? 'active' : ''}`} onClick={() => setMode('banner')}>By banner / area</button></li>
                <li className="nav-item"><button type="button" className={`nav-link py-1 px-3 ${mode === 'ids' ? 'active' : ''}`} onClick={() => setMode('ids')}>By Salesforce ids</button></li>
              </ul>
              <form className="mb-3" onSubmit={search}><div className="row g-2 align-items-end">
                {mode === 'banner' ? (
                  <>
                    <div className="col-12 col-md-4">
                      <label htmlFor="finder-bg" className="form-label small fw-semibold mb-1">Banner</label>
                      <input id="finder-bg" className="form-control" list="finder-banners" placeholder="Start typing… e.g. Home Hardware" autoComplete="off"
                        value={bg} onChange={e => setBg(e.target.value)} />
                      <datalist id="finder-banners">
                        {banners.map(b => <option key={b.value} value={b.value}>{b.count} store{b.count === 1 ? '' : 's'}</option>)}
                      </datalist>
                      {bg && !banners.some(b => b.value.toLowerCase() === bg.toLowerCase()) && (
                        <div className="form-text text-warning mb-0">No banner called “{bg}” — pick one from the list.</div>
                      )}
                    </div>
                    <div className="col-6 col-md-2">
                      <label htmlFor="finder-country" className="form-label small fw-semibold mb-1">Country</label>
                      <select id="finder-country" className="form-select" value={country} onChange={e => setCountry(e.target.value)}>
                        <option value="">— any —</option>
                        {countries.map(c => <option key={c.code} value={c.code}>{c.name} ({c.count})</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-2">
                      <label htmlFor="finder-region" className="form-label small fw-semibold mb-1">Region</label>
                      <input id="finder-region" className="form-control" placeholder="ON, Ontario…" value={region} onChange={e => setRegion(e.target.value)} />
                    </div>
                    <div className="col-6 col-md-2">
                      <label htmlFor="finder-city" className="form-label small fw-semibold mb-1">City</label>
                      <input id="finder-city" className="form-control" value={city} onChange={e => setCity(e.target.value)} />
                    </div>
                  </>
                ) : (
                  <div className="col-12 col-md-10">
                    <label htmlFor="finder-ids" className="form-label small fw-semibold mb-1">Salesforce account ids (comma, space or newline separated)</label>
                    <textarea id="finder-ids" className="form-control font-monospace" rows={2} value={ids} onChange={e => setIds(e.target.value)} />
                  </div>
                )}
                <div className="col-6 col-md-2 d-grid">
                  <button type="submit" className="btn btn-primary d-inline-flex align-items-center justify-content-center gap-1" disabled={searching}>
                    {searching ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />} Search
                  </button>
                </div>
              </div></form>
              {note && <div className="alert alert-warning py-2 small">{note}</div>}
              {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}

              {results && (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <div className="input-group input-group-sm" style={{ maxWidth: 340 }}>
                      <span className="input-group-text"><Search size={14} /></span>
                      <input className="form-control" placeholder="Filter these results: name, city, address, sfid…" value={filter} onChange={e => setFilter(e.target.value)} />
                      {filter && <button type="button" className="btn btn-outline-secondary" onClick={() => setFilter('')}><X size={13} /></button>}
                    </div>
                    <span className="small text-muted">{shown.length} of {results.length} shown · {ticked.size} ticked</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={shown.length === 0} onClick={toggleAll}>
                      {allShownTicked ? 'Untick all' : `Tick all ${shown.length} shown`}
                    </button>
                  </div>
                  <div className="table-responsive border rounded" style={{ maxHeight: '48vh' }}>
                    <table className="table table-sm table-hover align-middle mb-0 small">
                      <thead className="table-light position-sticky top-0">
                        <tr>
                          <th style={{ width: 32 }}><button type="button" className="btn btn-link p-0 text-dark" title={allShownTicked ? 'Untick all shown' : 'Tick all shown'} onClick={toggleAll}>{allShownTicked ? <CheckSquare size={16} /> : <Square size={16} />}</button></th>
                          <th>Store</th><th>City</th><th>Address</th><th>Email</th><th>sfid</th><th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.length === 0 && <tr><td colSpan={7} className="text-muted text-center py-3">{results.length === 0 ? 'No stores found.' : 'Nothing matches the filter.'}</td></tr>}
                        {shown.map(u => {
                          const k = key(u); const on = ticked.has(k);
                          return (
                            <tr key={k} className={on ? 'table-active' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleOne(u)}>
                              <td>{on ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-muted" />}</td>
                              <td className="fw-semibold">{u.name}{u.buyingGroup && u.buyingGroup !== u.name && <div className="text-muted fw-normal" style={{ fontSize: '0.85em' }}>{u.buyingGroup}</div>}</td>
                              <td>{[u.city, u.regionCode ?? u.region].filter(Boolean).join(', ')}</td>
                              <td className="text-muted">{[u.street, u.zip].filter(Boolean).join(' · ')}</td>
                              <td>{u.email || <span className="text-muted">—</span>}</td>
                              <td className="font-monospace">{u.sfid || u.id}</td>
                              <td>{statusOf(u)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {importing && <span className="small text-muted me-auto d-inline-flex align-items-center gap-1"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Adding {importing.done} / {importing.total}…</span>}
              <button type="button" className="btn btn-outline-secondary" disabled={!!importing} onClick={onClose}>Close</button>
              <button type="button" className="btn btn-primary" disabled={!!importing || ticked.size === 0} onClick={() => void run()}>
                Add {ticked.size || ''} {target ? `to ${target.name}` : 'as unassigned'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

export default LocationFinderModal;
